
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from './components/Header';
import DataTable from './components/DataTable';

export default function USPayroll() {
  const { user, userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [agents, setAgents] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showWarning, setShowWarning] = useState(true);

  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        setLoading(true);
        setError('');

        // Check if user is admin/owner
        const userRole = userProfile?.role?.toLowerCase();
        const adminAccess = userRole === 'admin' || userRole === 'owner';
        setIsAdmin(adminAccess);

        const { data: payrollData, error: payrollError } = await supabase
          .from('US Payroll')
          .select('*')
          .order('agent', { ascending: true });

        if (payrollError) {
          console.error('Error fetching US Payroll data:', payrollError);
          throw new Error('Failed to fetch US Payroll data from database');
        }

        console.log('US Payroll data fetched:', payrollData);
        
        // Separate total row from other rows
        const totalRow = (payrollData || []).find(row => row.agent?.toLowerCase() === 'total');
        const otherRows = (payrollData || []).filter(row => row.agent?.toLowerCase() !== 'total');
        
        // If not admin, filter to show only user's own data
        let userFilteredRows = otherRows;
        if (!adminAccess && user?.email) {
          // Extract first name from email (before @)
          const emailName = user.email.split('@')[0].toLowerCase();
          
          // Filter to only show rows where agent name matches user's email name
          userFilteredRows = otherRows.filter(row => {
            const agentName = row.agent?.toLowerCase() || '';
            return agentName.includes(emailName) || emailName.includes(agentName);
          });
          
          console.log('Filtered for user:', emailName, 'Found records:', userFilteredRows.length);
        }
        
        // Combine with total row at the end (only for admins viewing all)
        const sortedData = (adminAccess && totalRow) ? [...userFilteredRows, totalRow] : userFilteredRows;
        
        setData(sortedData);
        setFilteredData(sortedData);
        
        // Extract unique agent names for filter dropdown (excluding 'total')
        const uniqueAgents = Array.from(new Set(
          userFilteredRows
            .map(row => row.agent)
            .filter(agent => agent && agent !== '')
        )).sort();
        setAgents(uniqueAgents);
      } catch (err) {
        console.error('Error fetching payroll data:', err);
        setError('Failed to load payroll data from database.');
      } finally {
        setLoading(false);
      }
    };

    if (user && userProfile) {
      fetchPayrollData();
    }
  }, [user, userProfile]);

  // Filter data when agent selection changes
  useEffect(() => {
    if (selectedAgent === 'all') {
      setFilteredData(data);
    } else {
      // When filtering by agent, exclude the total row
      const filtered = data.filter(row => 
        row.agent === selectedAgent && row.agent?.toLowerCase() !== 'total'
      );
      setFilteredData(filtered);
    }
  }, [selectedAgent, data]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleAgreeWarning = () => {
    setShowWarning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payroll data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-400">
      <Header onRefresh={handleRefresh} isConnected={!error} />

      {/* Warning Popup */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-yellow-600 text-3xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Important Notice</h2>
              <p className="text-gray-700 leading-relaxed">
                By selecting I agree, you understand that this is not a true paycheck stat. This is subject to change and is estimated based on the most recent paycheck you are about to get.
              </p>
            </div>
            
            <button
              onClick={handleAgreeWarning}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
            >
              <i className="ri-checkbox-circle-line text-xl"></i>
              <span>I Agree</span>
            </button>
          </div>
        </div>
      )}

      <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="ri-warning-line text-yellow-600 mr-2"></i>
              <p className="text-yellow-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Agent Filter Section - Only show for admins with multiple agents */}
        {isAdmin && agents.length > 1 && (
          <div className="mb-6 bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <i className="ri-filter-3-line text-green-600 text-xl"></i>
                <h2 className="text-lg font-semibold text-gray-900">Filter by Agent</h2>
              </div>
              
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700">Select Agent:</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white min-w-[200px]"
                >
                  <option value="all">All Agents ({data.length})</option>
                  {agents.map((agent) => (
                    <option key={agent} value={agent}>
                      {agent}
                    </option>
                  ))}
                </select>
                
                {selectedAgent !== 'all' && (
                  <button
                    onClick={() => setSelectedAgent('all')}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors flex items-center space-x-1"
                  >
                    <i className="ri-close-line"></i>
                    <span>Clear</span>
                  </button>
                )}
              </div>
            </div>
            
            {selectedAgent !== 'all' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-green-600">{filteredData.length}</span> record{filteredData.length !== 1 ? 's' : ''} for <span className="font-semibold">{selectedAgent}</span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              {isAdmin ? 'US Payroll Data' : 'My Payroll Data'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {isAdmin ? (
                selectedAgent === 'all' 
                  ? `Showing all ${filteredData.length} payroll records`
                  : `Filtered view for ${selectedAgent}`
              ) : (
                `Your personal payroll information`
              )}
            </p>
          </div>
          
          {filteredData.length > 0 ? (
            <DataTable data={filteredData} />
          ) : (
            <div className="p-8 text-center">
              <i className="ri-file-list-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-500">No payroll data available</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            © 2013 Driven Insurance Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
